import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { Tabs, TabsContent, TabsList, TabsTrigger, Tooltip } from '@ludus/ui'
import type { LeadId, LudusLeadId, PipelineId } from '@ludus/core'
import { fetchLeadDetail } from '@/lib/api/lead-detail.ts'
import { ActivityTab } from './ActivityTab.tsx'
import { EmptyState, SourceDetailTab } from './SourceDetailTab.tsx'
import { SourceEmbedTab } from './SourceEmbedTab.tsx'
import { useSiblingDetail } from './useSiblingDetail.ts'

const DISABLED_TABS = ['notes', 'tasks', 'emails'] as const

interface LeadTabsShellProps {
  pipelineId: PipelineId
  leadId: LeadId
  /** `null` for a lead typed into Ludus — there is no source record to describe. */
  externalRef: string | null
  /** The cycle this card sits on, when it sits on one — the sibling seam (Q17). */
  ludusLeadId: LudusLeadId | null
}

/**
 * The Source and Preview tabs appear only when the attached adapter actually
 * answered — a native pipeline, an adapter that does not implement
 * `describeLead`, and a hand-typed lead all produce no document, and each shows
 * one tab fewer rather than an empty tab explaining itself.
 *
 * Neither tab knows which source it is rendering. `SourceDetailTab` prints the
 * labels the adapter wrote and `SourceEmbedTab` frames the URL it composed, so
 * the next source arrives without touching this file.
 */
export function LeadTabsShell({
  pipelineId,
  leadId,
  externalRef,
  ludusLeadId,
}: LeadTabsShellProps) {
  const { t } = useTranslation()
  const [tab, setTab] = useState('activity')
  const comingSoon = t('pages.leads.detail.actions.comingSoon', {
    defaultValue: 'Coming in a later milestone',
  })

  const { data: detail, error } = useSWR(
    externalRef === null ? null : `/pipelines/${pipelineId}/leads/${leadId}/detail`,
    () => fetchLeadDetail(pipelineId, leadId),
  )

  // Unresolved and "the source has nothing" are the same to the tab list, which
  // is why this reads the data rather than a loading flag: a tab that appears a
  // beat after the page settles is worse than one that appears with it. A
  // failure is not the same, though — the tab carries the "could not be read"
  // state, so hiding it there is the one outcome that owes the operator a word.
  const hasOwnDetail = detail != null || error != null

  // A derived card has no describe of its own, so it borrows its sibling's
  // (Q17). Asked for only when this card has none — a card that can describe
  // itself never pays for the sale read.
  const sibling = useSiblingDetail(ludusLeadId, leadId, externalRef === null)

  const hasDetail = hasOwnDetail || sibling.detail != null
  // The sibling's page is the derived card's page too: a Delivery card is *about*
  // the order, and the order's source is where that order is rendered.
  const embedUrl = detail?.embedUrl ?? sibling.detail?.embedUrl ?? null

  return (
    <Tabs value={tab} onValueChange={setTab} className="flex flex-col gap-3">
      <TabsList>
        <TabsTrigger value="activity">
          {t('pages.leads.detail.tabs.activity', { defaultValue: 'Activity' })}
        </TabsTrigger>
        {hasDetail && (
          <TabsTrigger value="source">
            {t('pages.leads.detail.tabs.source', { defaultValue: 'Details' })}
          </TabsTrigger>
        )}
        {embedUrl !== null && (
          <TabsTrigger value="preview">
            {t('pages.leads.detail.tabs.preview', { defaultValue: 'Preview' })}
          </TabsTrigger>
        )}
        {DISABLED_TABS.map((key) => (
          <Tooltip key={key} content={comingSoon}>
            <TabsTrigger value={key} disabled>
              {t(`pages.leads.detail.tabs.${key}`, { defaultValue: capitalize(key) })}
            </TabsTrigger>
          </Tooltip>
        ))}
      </TabsList>

      <TabsContent value="activity">
        <ActivityTab pipelineId={pipelineId} leadId={leadId} />
      </TabsContent>

      {hasDetail && (
        <TabsContent value="source">
          {detail != null ? (
            <SourceDetailTab pipelineId={pipelineId} leadId={leadId} detail={detail} />
          ) : sibling.detail != null &&
            sibling.siblingLeadId !== null &&
            sibling.siblingPipelineId !== null ? (
            <div className="flex flex-col gap-2">
              {/* Said out loud, because whose sheet this is changes what it
                  means: these are the source's words about the *sibling* card,
                  not about the card being looked at. */}
              <p className="text-[11px] text-muted">
                {t('pages.leads.detail.source.fromSibling', {
                  board: sibling.siblingBoardName ?? '',
                  defaultValue: 'From the linked card on {{board}}, on the same sale.',
                })}
              </p>
              {/* Addressed by the sibling's own board: its photos come through
                  that board's adapter, and the media proxy answers only for the
                  pipeline that holds the record. */}
              <SourceDetailTab
                pipelineId={sibling.siblingPipelineId}
                leadId={sibling.siblingLeadId}
                detail={sibling.detail}
              />
            </div>
          ) : (
            <EmptyState
              tone="error"
              text={t('pages.leads.detail.source.error', {
                defaultValue: 'The source could not be read.',
              })}
            />
          )}
        </TabsContent>
      )}

      {embedUrl !== null && (
        <TabsContent value="preview">
          <SourceEmbedTab url={embedUrl} />
        </TabsContent>
      )}
    </Tabs>
  )
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
